import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import styled from 'styled-components';
import './styles/App.css';
import VirtualizedVideoFeed from './components/VirtualizedVideoFeed';
import PublicationSelector from './components/PublicationSelector';
import VideoLibrary from './components/VideoLibrary';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import OnScreenDebugConsole from './components/OnScreenDebugConsole';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminPortal from './components/AdminPortal';
import AgencyRoute from './components/AgencyRoute';
import AgencyEntry from './components/AgencyEntry';
import AgencyApply from './components/AgencyApply';
import AgencyLoginPage from './components/AgencyLoginPage';
import AgencyDashboard from './components/AgencyDashboard';
import AgencyInviteAccept from './components/AgencyInviteAccept';
import PublisherInviteAccept from './components/PublisherInviteAccept';
import SubscriberJoinPage from './components/SubscriberJoinPage';
import PublisherRoute from './components/PublisherRoute';
import PublisherDashboard from './components/PublisherDashboard';
import PublisherLoginPage from './components/PublisherLoginPage';
import UserAvatar from './components/UserAvatar';
// import SoundPrompt from './components/SoundPrompt'; // Modern TikTok approach: No sound prompts
import { FaSync, FaCreditCard, FaArrowLeft } from 'react-icons/fa';
import { usePoints } from './hooks/PointsContext';
import { postInviteClick } from './api/videoService';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${props => props.isAdminRoute ? `
    height: auto;
    min-height: 100vh;
    width: 100vw;
    overflow: auto;
  ` : `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  `}
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  z-index: 100;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PointsDisplay = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${p => p.$critical ? '#ff4757' : p.$low ? '#f0a020' : '#fff'};
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: ${p => p.$critical ? '#ff4757' : p.$low ? '#f0a020' : '#2ecc71'};
  }
  
  span {
    font-size: 1.2rem;
    opacity: 0.9;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshButton = styled.button`
  background-color: #ff4757;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: #ff6b81;
    transform: rotate(45deg);
  }
  
  &:active {
    transform: translateY(1px) rotate(45deg);
  }
  
  svg {
    font-size: 20px;
  }
  
  &.refreshing {
    background-color: #2f3542;
    pointer-events: none;
    
    svg {
      animation: spin 1s linear infinite;
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Content = styled.main`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;


// Session storage key for subscriber ID
const SUBSCRIBER_ID_KEY = 'vusub_subscriber_id';

// Utility function to handle subscriber ID (sid) parameter
const INVITE_TOKEN_KEY = 'vusub_pending_invite';
const INVITE_PUB_KEY = 'vusub_pending_invite_pub';
const OPEN_INVITE_TOKEN_KEY = 'vusub_pending_open_invite';
const INVITE_CLICK_ID_KEY = 'vusub_invite_click_id';

const handleSubscriberIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sid = urlParams.get('sid');
  const inviteToken = urlParams.get('invite');
  
  if (sid) {
    sessionStorage.setItem(SUBSCRIBER_ID_KEY, sid);
  }
  if (inviteToken) {
    sessionStorage.setItem(INVITE_TOKEN_KEY, inviteToken);
    if (sid) sessionStorage.setItem(INVITE_PUB_KEY, sid);
    // Fire the direct invite-click beacon (fire-and-forget; never blocks render).
    // Stash the returned click_id so activate-invite can mark this click converted.
    postInviteClick(inviteToken, 'direct', sid || undefined).then(clickId => {
      if (clickId) {
        try { sessionStorage.setItem(INVITE_CLICK_ID_KEY, clickId); } catch { /* ignore */ }
      }
    });
  }
  // Detect /join/:token path for open invite links
  const pathParts = window.location.pathname.split('/');
  if (pathParts[1] === 'join' && pathParts[2]) {
    sessionStorage.setItem(OPEN_INVITE_TOKEN_KEY, pathParts[2]);
  }
  if (sid || inviteToken) {
    const url = new URL(window.location);
    url.searchParams.delete('sid');
    url.searchParams.delete('invite');
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  }
};

// Utility function to get stored subscriber ID
const getSubscriberId = () => {
  return sessionStorage.getItem(SUBSCRIBER_ID_KEY);
};

// VideoFeedWrapper component to handle publication-specific feeds
const VideoFeedWrapper = () => {
  const { publicationId } = useParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const videoFeedRef = useRef(null);
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  
  // Get points from context
  const pointsBalance = usePoints();
  const { initBalance } = pointsBalance;

  // Fetch real balance from server once we have the publication and account
  useEffect(() => {
    const account = accounts[0];
    if (!account || !publicationId) return;
    // Persist account id for analytics attribution
    try { sessionStorage.setItem('vusub_account_id', account.localAccountId || account.homeAccountId || ''); } catch { /* ignore */ }
    instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account })
      .then(result => result.idToken || instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account, forceRefresh: true }).then(r => r.idToken))
      .then(idToken => { if (idToken) initBalance(publicationId, idToken); })
      .catch(() => {});
  }, [accounts, instance, publicationId, initBalance]);
  
  // Audio context available but not currently used (testing simple autoplay approach)
  // const { triggerSoundPrompt } = useAudio();
  
  // Handle refresh button click
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    if (videoFeedRef.current && videoFeedRef.current.refresh) {
      videoFeedRef.current.refresh()
        .then(() => setIsRefreshing(false))
        .catch(() => setIsRefreshing(false));
    } else {
      setIsRefreshing(false);
    }
  }, []);
  
  const handleBack = () => {
    navigate('/');
  };
  
  return (
    <>
      <Header className="app-header">
        <BackButton onClick={handleBack} title="Back to publications">
          <FaArrowLeft />
        </BackButton>
        <PointsDisplay
          className="points-display"
          $low={pointsBalance.balance > 0 && pointsBalance.balance <= 5}
          $critical={pointsBalance.balance <= 0}
        >
          <FaCreditCard />
          <span>Balance: {pointsBalance.balance}</span>
        </PointsDisplay>
        <HeaderRight>
          <RefreshButton
            onClick={handleRefresh}
            className={isRefreshing ? 'refreshing' : ''}
            disabled={isRefreshing}
            title="Refresh videos"
          >
            <FaSync />
          </RefreshButton>
          <UserAvatar />
        </HeaderRight>
      </Header>
      
      <Content className="app-content">
        <VirtualizedVideoFeed ref={videoFeedRef} publicationId={publicationId} />
      </Content>
    </>
  );
};

/**
 * Main App component
 * 
 * Note on Auto-Refresh Feature:
 * - There's an issue where videos don't load properly on initial page load
 * - To fix this, we've added an automatic refresh that runs once, 3 seconds after page load
 * - This can be disabled by setting AUTO_REFRESH_ENABLED to false above
 * - The delay can be adjusted by changing AUTO_REFRESH_DELAY_MS
 * - This is a workaround for a race condition that was occurring when trying to refresh immediately
 */
const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

function App() {
  // App now serves as a router container
  const location = useLocation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const isAdminRoute = ['/admin', '/analytics', '/vusub-internal', '/agency/dashboard', '/agency/login', '/agency/apply', '/agency/invite', '/agency', '/publisher', '/join'].some(p => location.pathname.startsWith(p));

  // Handle subscriber ID and invite token from URL on app load
  useEffect(() => {
    handleSubscriberIdFromUrl();
  }, []);

  // Activate pending subscriber invite once MSAL account is available
  useEffect(() => {
    const account = accounts[0];
    if (!account) return;
    const inviteToken = sessionStorage.getItem(INVITE_TOKEN_KEY);
    if (!inviteToken) return;
    sessionStorage.removeItem(INVITE_TOKEN_KEY);
    sessionStorage.removeItem(INVITE_PUB_KEY);
    instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account })
      .then(result => {
        if (result.idToken) return result.idToken;
        return instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account, forceRefresh: true })
          .then(r => r.idToken);
      })
      .then(idToken => {
        if (!idToken) return;
        const clickId = sessionStorage.getItem(INVITE_CLICK_ID_KEY);
        const activateBody = { token: inviteToken };
        if (clickId) activateBody.click_id = clickId;
        fetch(`${API_URL}/api/subscriber/activate-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify(activateBody),
        }).catch(() => {});
      })
      .catch(() => {});
  }, [accounts, instance]);

  // Activate pending open (general) invite once MSAL account is available
  useEffect(() => {
    const account = accounts[0];
    if (!account) return;
    const openInviteToken = sessionStorage.getItem(OPEN_INVITE_TOKEN_KEY);
    if (!openInviteToken) return;
    sessionStorage.removeItem(OPEN_INVITE_TOKEN_KEY);
    instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account })
      .then(result => {
        if (result.idToken) return result.idToken;
        return instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account, forceRefresh: true })
          .then(r => r.idToken);
      })
      .then(idToken => {
        if (!idToken) return;
        const clickId = sessionStorage.getItem(INVITE_CLICK_ID_KEY);
        return fetch(`${API_URL}/api/subscriber/open-invite/${encodeURIComponent(openInviteToken)}/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify(clickId ? { click_id: clickId } : {}),
        }).then(r => r.json()).then(data => {
          if (data.publication_id) navigate(`/feed/${data.publication_id}`);
        });
      })
      .catch(() => {});
  }, [accounts, instance, navigate]);

  return (
    <AppContainer className={`app ${isAdminRoute ? 'admin-route' : ''}`} isAdminRoute={isAdminRoute}>
      {/* Internal admin portal — bypasses CIAM, has its own login */}
      <Routes>
        <Route path="/vusub-internal" element={<AdminPortal />} />
        {/* Agency auth routes — no MSAL required */}
        <Route path="/agency/login" element={<AgencyLoginPage />} />
        <Route path="/agency/invite/:token" element={<AgencyInviteAccept />} />
        <Route path="/publisher/login" element={<PublisherLoginPage />} />
        <Route path="/publisher/invite/:token" element={<PublisherInviteAccept />} />
        {/* General subscriber invite links — no auth required for landing page */}
        <Route path="/join/:token" element={<SubscriberJoinPage />} />
        <Route path="/publisher/dashboard" element={<PublisherRoute><PublisherDashboard /></PublisherRoute>} />
        <Route path="/publisher/invite-clicks" element={<PublisherRoute><PublisherDashboard initialView="invite-clicks" /></PublisherRoute>} />
        <Route path="/agency/dashboard" element={<AgencyRoute><AgencyDashboard /></AgencyRoute>} />
        <Route path="*" element={
          <ProtectedRoute>
            <Routes>
          {/* Publication selector as the default landing page */}
          <Route path="/" element={<PublicationSelector />} />

          {/* Video feed for a specific publication */}
          <Route path="/feed/:publicationId" element={<VideoFeedWrapper />} />

          {/* Video Library — admin only */}
          <Route path="/admin" element={<AdminRoute><VideoLibrary /></AdminRoute>} />

          {/* Analytics Dashboard — admin only */}
          <Route path="/analytics" element={<AdminRoute><AnalyticsDashboard /></AdminRoute>} />

          {/* Agency portal */}
          <Route path="/agency/apply" element={<AgencyApply />} />
          <Route path="/agency" element={<AgencyEntry />} />

          {/* Redirect legacy route to the publication selector */}
          <Route path="/feed" element={<Navigate to="/" replace />} />

          {/* Redirect any other routes to the publication selector */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ProtectedRoute>
        } />
      </Routes>
      
      {/* On-screen debug console for mobile debugging */}
      <OnScreenDebugConsole />
      
      {/* Modern TikTok approach: No sound prompts needed - audio unlocks on first interaction */}
    </AppContainer>
  );
}

// Export the getSubscriberId function for use in other components
export { getSubscriberId };

export default App;
